import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  batchAdminApi,
  type BatchJob,
  type BatchLastRun,
  type BatchRecipient,
  type ListKey,
  type SmsConfig,
} from '../../api/serverCheck.js';
import { Spinner, ErrorBox, Field } from '../../components/ui.js';
import { inputCls } from '../../lib/formValidation.js';
import { describeCron, nextRun, untilText } from '../../lib/cron.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { phone as formatPhone } from '../../lib/format.js';

/**
 * 서버체크 > 배치관리.
 *
 * 레거시 PHP 배치 20개를 Node 로 옮겨 crontab 에 등록해 뒀는데, 전부 `#` 로
 * 꺼져 있었다. 이 화면이 그것을 켜고 끄고 주기를 바꾼다.
 *
 * ── 저장 버튼을 누르기 전에는 아무것도 안 바뀐다 ────────────────────────────
 * 체크박스를 만질 때마다 시스템 crontab 을 고치면 실수 한 번이 바로 운영에
 * 반영된다. 화면에서 원하는 상태를 다 만든 뒤 한 번에 저장한다.
 *
 * ── 편집 중인 값은 서버 응답이 덮지 않는다 ──────────────────────────────────
 * `edits` 는 **손댄 job 만** 담는다(전체 복사본이 아니다). 예전에는 목록을
 * 통째로 초안에 복사하고 응답이 올 때마다 다시 덮었는데, react-query 가 창을
 * 다시 활성화할 때 자동으로 재조회하므로 - 스케줄 몇 개를 고쳐 두고 잠깐 다른
 * 탭을 보고 오면 **고친 내용이 조용히 사라졌다.**
 *
 * SMS/LMS 를 보내는 배치는 수신번호를 여기서 함께 관리한다 - 담당자가 바뀔
 * 때마다 코드를 고쳐 배포하던 것을 화면으로 옮겼다.
 */

interface Draft {
  schedule: string;
  enabled: boolean;
}

/**
 * 저장 전 검사. 서버(crontab.logic.ts 의 isValidSchedule)와 같은 규칙이다.
 * 한 칸이라도 틀리면 저장 요청 전체가 400 으로 거절되므로 여기서 먼저 잡는다.
 */
function checkSchedule(schedule: string): string | undefined {
  const fields = schedule.trim().split(/\s+/).filter(Boolean);
  if (fields.length !== 5) return '분 시 일 월 요일, 다섯 칸이어야 합니다.';
  if (!fields.every((f) => /^[0-9*/,-]+$/.test(f))) {
    return '숫자와 * / , - 만 쓸 수 있습니다.';
  }
  return undefined;
}

export function BatchAdminPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // 쓰기는 최고 관리자만. 서버도 requireRole('admin') 으로 다시 막는다.
  const canWrite = (user?.authtype ?? '').split(',').map((s) => s.trim()).includes('admin');

  const list = useQuery({ queryKey: ['batch-admin'], queryFn: batchAdminApi.list });

  /** 손댄 job 만. 없는 키는 "서버 값 그대로"를 뜻한다. */
  const [edits, setEdits] = useState<Record<string, Draft>>({});
  const [openRecipients, setOpenRecipients] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (rows: { name: string; schedule: string; enabled: boolean }[]) =>
      batchAdminApi.save(rows),
    onSuccess: async () => {
      // 저장된 값이 곧 서버 값이 되므로 편집 흔적을 비운다. 비우지 않으면
      // 재조회가 끝날 때까지 같은 내용이 '변경됨'으로 남는다.
      setEdits({});
      await qc.invalidateQueries({ queryKey: ['batch-admin'] });
    },
  });

  if (list.isLoading) return <Spinner />;
  if (list.error) return <ErrorBox error={list.error} onRetry={() => list.refetch()} />;
  if (!list.data) return null;

  const jobs = list.data.jobs;

  /** 화면이 쓰는 유일한 파생값. 표·개수·저장 목록이 전부 여기서 나온다. */
  const rows = jobs.map((job) => {
    const draft: Draft = edits[job.name] ?? { schedule: job.schedule, enabled: job.enabled };
    return {
      job,
      draft,
      dirty: draft.enabled !== job.enabled || draft.schedule !== job.schedule,
      scheduleError: checkSchedule(draft.schedule),
    };
  });

  const changed = rows.filter((r) => r.dirty);
  const enabledCount = rows.filter((r) => r.draft.enabled).length;
  const invalid = rows.filter((r) => r.scheduleError !== undefined);
  /** 새로 켜는 배치 - 저장 전에 한 번 더 확인한다. */
  const turningOn = changed.filter((r) => r.draft.enabled && !r.job.enabled);
  const failedCount = jobs.filter((j) => j.lastRun?.status === 'failed').length;

  const set = (job: BatchJob, patch: Partial<Draft>) =>
    setEdits((d) => ({
      ...d,
      [job.name]: {
        ...(d[job.name] ?? { schedule: job.schedule, enabled: job.enabled }),
        ...patch,
      },
    }));

  return (
    <div className="page">
      <header className="page-head">
        <h1>배치관리</h1>
        <p className="page-desc">
          crontab 에 등록된 배치 {jobs.length}개 · 켜짐 <b>{enabledCount}개</b> · 문자 발송{' '}
          {jobs.filter((j) => j.sends).length}개
          {failedCount > 0 && (
            <>
              {' · '}
              <b className="batch-desc-fail">마지막 실행 실패 {failedCount}개</b>
            </>
          )}
        </p>
      </header>

      <div className="notice notice--warn">
        <b>배치를 켜면 실제로 동작합니다.</b> 감시 배치는 담당자 휴대폰으로 문자를 보내고,
        집계 배치는 운영 DB에 씁니다. 레거시 PHP 크론이 같은 일을 하고 있다면{' '}
        <b>같은 배치를 양쪽에서 돌리지 않도록</b> 레거시 쪽 줄을 먼저 지우세요.
      </div>

      {/* === false 로 본다 - 아직 이 필드를 안 주는 옛 서버에 붙었을 때
          없는 값을 "준비 안 됨"으로 읽어 헛경보를 띄우지 않도록. */}
      {list.data.runStoreReady === false && (
        <div className="notice notice--warn">
          <b>지금 배치를 켜면 전부 실패합니다.</b> 실행 이력 테이블(
          <code>xadmin_batch_run</code>)이 아직 없습니다. 배치는 일을 시작하기 전에 이 테이블에
          실행 기록을 남기는데, 테이블이 없으면 그 첫 단계에서 죽습니다 — 크론은 제 시각에 돌고,
          실패는 <code>server/log/&lt;배치이름&gt;.log</code> 에만 남습니다. 켜기 전에{' '}
          <code>migrations/003_xadmin_batch_run.sql</code> 을 적용하세요.
        </div>
      )}

      {list.data.unknown.length > 0 && (
        <div className="notice notice--warn">
          crontab 에 이 화면이 모르는 배치가 있습니다: {list.data.unknown.join(', ')} — 누가 직접
          넣었거나 목록에서 빠진 것입니다. 저장하면 그 줄은 사라집니다.
        </div>
      )}

      {!list.data.recipientStoreReady && (
        <div className="notice">
          수신번호 테이블이 아직 적용되지 않아 <b>지금은 소스에 있는 기본 번호로 발송</b>됩니다.
          화면에서 고치려면 <code>migrations/011_xadmin_batch_sms_recipient.sql</code> 을 DBA가
          적용해야 합니다.
        </div>
      )}

      <div className="batch-table-wrap">
      <table className="table batch-table">
        <thead>
          <tr>
            <th style={{ width: 62 }}>실행</th>
            <th>배치</th>
            <th style={{ width: 210 }}>실행 주기</th>
            <th style={{ width: 170 }}>마지막 실행</th>
            <th style={{ width: 150 }}>문자</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ job, draft, dirty, scheduleError }) => {
            const blocked = job.blockedReason !== null;
            const upcoming = scheduleError === undefined ? nextRun(draft.schedule) : null;
            return (
              <tr
                key={job.name}
                className={`${draft.enabled ? 'is-on' : 'is-off'}${dirty ? ' is-dirty' : ''}`}
              >
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${job.title} 실행`}
                    checked={draft.enabled}
                    disabled={!canWrite || blocked}
                    onChange={(e) => set(job, { enabled: e.target.checked })}
                  />
                </td>
                <td>
                  <div className="batch-title">
                    {job.title}
                    {job.sends && (
                      <span className="badge badge--info">{job.sends.toUpperCase()} 발송</span>
                    )}
                    {dirty && <span className="badge">변경됨</span>}
                  </div>
                  <div className="td-muted batch-summary">{job.summary}</div>
                  <code className="batch-name">{job.name}</code>
                  {blocked && (
                    <div className="batch-blocked">켤 수 없음 — {job.blockedReason}</div>
                  )}
                  {!blocked && job.caution && (
                    <div className="batch-caution">주의 — {job.caution}</div>
                  )}
                </td>
                <td>
                  <input
                    className={inputCls(scheduleError)}
                    aria-label={`${job.title} 실행 주기`}
                    value={draft.schedule}
                    disabled={!canWrite}
                    onChange={(e) => set(job, { schedule: e.target.value })}
                  />
                  {scheduleError !== undefined ? (
                    <div className="batch-blocked">{scheduleError}</div>
                  ) : (
                    // 크론을 읽을 줄 몰라도 알 수 있게 풀어 준다. 켜져 있는
                    // 배치는 다음에 언제 도는지까지 보여준다.
                    <div className="batch-when">
                      <b>{describeCron(draft.schedule) ?? '주기를 읽을 수 없습니다'}</b>
                      {draft.enabled && upcoming && (
                        <span className="td-muted">
                          {' · 다음 '}
                          {upcoming.toLocaleString('ko-KR', {
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {` (${untilText(upcoming)})`}
                        </span>
                      )}
                    </div>
                  )}
                  {draft.schedule !== job.defaultSchedule && (
                    <div className="td-muted batch-default">기본값 {job.defaultSchedule}</div>
                  )}
                </td>
                <td>
                  <LastRunCell run={job.lastRun} enabled={job.enabled} />
                </td>
                <td>
                  {job.sends ? (
                    <div className="batch-actions-cell">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOpenRecipients(job.name)}
                      >
                        수신번호
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!canWrite}
                        title={canWrite ? undefined : '최고 관리자만 볼 수 있습니다.'}
                        onClick={() => setOpenPreview(job.name)}
                      >
                        보낼 내용
                      </button>
                    </div>
                  ) : (
                    <span className="td-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <ErrorBox error={save.error} />

      <div className="batch-actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!canWrite || changed.length === 0 || invalid.length > 0 || save.isPending}
          onClick={() => {
            // 켜는 순간 문자가 나가고 운영 DB 에 쓰기 시작한다. 끄거나 주기만
            // 바꾸는 저장은 되돌릴 수 있어서 묻지 않는다 - 켜는 것만 묻는다.
            if (turningOn.length > 0) {
              const names = turningOn.map((r) => `· ${r.job.title}`).join('\n');
              const sends = turningOn.filter((r) => r.job.sends).length;
              const warn = sends > 0 ? `\n\n이 중 ${sends}개는 담당자에게 문자를 보냅니다.` : '';
              if (!confirm(`아래 배치를 켭니다. 저장하면 다음 실행 시각부터 실제로 동작합니다.\n\n${names}${warn}`)) {
                return;
              }
            }
            save.mutate(
              rows.map(({ job, draft }) => ({
                name: job.name,
                schedule: draft.schedule,
                enabled: draft.enabled,
              })),
            );
          }}
        >
          {save.isPending ? '저장 중…' : `crontab에 저장 (${changed.length}건 변경)`}
        </button>
        {changed.length > 0 && (
          <button type="button" className="btn btn--ghost" onClick={() => setEdits({})}>
            되돌리기
          </button>
        )}
        {!canWrite && <span className="td-muted">최고 관리자만 바꿀 수 있습니다.</span>}
        {invalid.length > 0 && (
          <span className="td-muted">실행 주기가 올바르지 않은 배치가 {invalid.length}개 있습니다.</span>
        )}
        {save.isSuccess && changed.length === 0 && (
          <span className="batch-saved">저장했습니다.</span>
        )}
      </div>

      <p className="td-muted batch-foot">
        저장하면 <code>{list.data.root}</code> 의 crontab 관리 구역만 다시 씁니다. 배치와 무관한
        줄은 건드리지 않고, 바꾸기 전 내용은 <code>server/log/crontab-backup/</code> 에 남습니다.
      </p>

      <SmsConfigCard canWrite={canWrite} jobs={jobs} />

      {canWrite && (
        <p className="td-muted batch-foot">
          배치가 실제로 보낸 문자는{' '}
          <Link to="/server-check/sms-log">
            <b>문자 발송 이력</b>
          </Link>{' '}
          에서 봅니다 — 언제 누구에게 나갔는지, 실패했다면 그 이유까지 남습니다.
        </p>
      )}

      {openPreview && (
        <PreviewDialog
          job={jobs.find((j) => j.name === openPreview)!}
          onClose={() => setOpenPreview(null)}
        />
      )}

      {openRecipients && (
        <RecipientDialog
          job={jobs.find((j) => j.name === openRecipients)!}
          canWrite={canWrite}
          onClose={() => setOpenRecipients(null)}
        />
      )}
    </div>
  );
}

/**
 * 마지막 실행 한 칸.
 *
 * 이 표(xadmin_batch_run)는 003 으로 만들어 두고도 화면이 한 번도 읽지 않았다 -
 * 배치를 켠 뒤 "잘 돌고 있나"를 보려면 서버에 들어가 로그 파일을 열어야 했다.
 * 켜는 화면과 결과를 보는 자리가 떨어져 있으면 아무도 결과를 보지 않는다.
 */
function LastRunCell({ run, enabled }: { run: BatchLastRun | null; enabled: boolean }) {
  if (!run) {
    return (
      <span className="td-muted">
        {enabled ? '아직 기록 없음' : '—'}
      </span>
    );
  }

  const tone = {
    succeeded: 'badge--ok',
    failed: 'badge--danger',
    running: 'badge--info',
    skipped: 'badge',
  }[run.status];
  const label = {
    succeeded: '성공',
    failed: '실패',
    running: '실행 중',
    skipped: '건너뜀',
  }[run.status];

  return (
    <div className="batch-lastrun">
      <div className="batch-title">
        <span className={`badge ${tone}`}>{label}</span>
        <span className="td-muted">{(run.startedAt ?? run.scheduledFor).slice(5, 16)}</span>
      </div>
      {run.status === 'failed' && run.errorMessage && (
        // 실패 이유가 여기 안 보이면 결국 로그 파일을 열게 된다.
        <div className="batch-blocked" title={run.errorMessage}>
          {run.errorMessage}
        </div>
      )}
      {run.status === 'succeeded' && (run.notificationCount > 0 || run.writeCount > 0) && (
        <div className="td-muted batch-default">
          {run.notificationCount > 0 && `문자 ${run.notificationCount}통`}
          {run.notificationCount > 0 && run.writeCount > 0 && ' · '}
          {run.writeCount > 0 && `쓰기 ${run.writeCount}건`}
        </div>
      )}
    </div>
  );
}

/**
 * 보낼 내용 확인.
 *
 * 배치를 **dryRun 으로 실제 실행**해서 그 배치가 만든 문안을 그대로 보여준다.
 * 형식 예시를 따로 적어 두지 않은 이유는 그 예시가 코드와 따로 늙기 때문이다 -
 * 정작 확인이 필요한 순간에 실제와 다른 것을 보여주게 된다.
 *
 * 열자마자 돌지 않고 **버튼을 눌러야** 조회한다. 발송과 DB 쓰기는 없지만
 * 읽기 부하는 실제 실행과 같아서, 화면을 열어 두는 것만으로 운영 DB 에
 * 질의가 나가면 안 된다.
 */
function PreviewDialog({ job, onClose }: { job: BatchJob; onClose: () => void }) {
  const titleId = useId();
  const preview = useMutation({ mutationFn: () => batchAdminApi.preview(job.name) });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const result = preview.data;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title" id={titleId}>
          {job.title} — 보낼 내용
        </h2>
        <p className="td-muted">{job.summary}</p>

        <div className="notice">
          <b>지금 데이터로 문안만 만들어 봅니다.</b> 문자는 보내지 않고 DB 에도 쓰지 않습니다.
          다만 조회는 실제 실행과 똑같이 하므로 결과가 나오기까지 시간이 걸릴 수 있습니다.
        </div>

        <ErrorBox error={preview.error} />

        <div className="batch-actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? '만드는 중…' : result ? '다시 만들기' : '지금 문안 만들기'}
          </button>
          {result && (
            <span className="td-muted">
              {result.scheduledFor} 기준 · {result.elapsedMs.toLocaleString()}ms
            </span>
          )}
        </div>

        {preview.isPending && <Spinner label="배치를 돌려 문안을 만드는 중…" />}

        {result &&
          (result.messages.length === 0 ? (
            <p className="notice">
              지금은 <b>보낼 내용이 없습니다.</b> 이 배치는 보낼 조건이 맞을 때만 문자를 보냅니다
              (예: 디스크 감시는 임계치를 넘었을 때). 조건이 맞는 시각에 다시 확인해보세요.
            </p>
          ) : (
            result.messages.map((m, i) => (
              <section key={`${m.label}-${i}`} className="batch-preview">
                <div className="batch-title">
                  {m.label}
                  <span className="badge badge--info">{job.sends?.toUpperCase()}</span>
                  <span className="td-muted batch-preview__len">{m.text.length}자</span>
                </div>
                {m.text === '' ? (
                  <p className="batch-blocked">
                    빈 문안입니다 — 이대로 나가면 받는 사람에게 빈 문자가 갑니다.
                  </p>
                ) : (
                  // 줄바꿈이 실제 문자와 같이 보여야 한다(white-space: pre-wrap).
                  <pre className="batch-preview__body">{m.text}</pre>
                )}
              </section>
            ))
          ))}
      </div>
    </div>
  );
}

/**
 * SMS/LMS 발송 API 설정.
 *
 * 여기서 고치는 값은 **문자를 보내는 배치 전부**에 적용된다(수신번호가 job
 * 마다 따로인 것과 다르다). 예전에는 소스 상수라 API 키를 교체할 때마다
 * 빌드·재기동이 필요했고, 그 사이 감시 알림이 전부 실패했다.
 *
 * API 키는 서버가 **가려서** 내려준다. 그래서 화면은 지금 키의 온전한 값을
 * 모르고, 저장할 때 키 칸을 비워 두면 서버가 기존 키를 그대로 둔다.
 *
 * 발신번호만은 배치마다 따로 둘 수 있다(수신번호 창에서 고친다). 여기서는
 * 따로 정한 배치가 있으면 그 목록을 함께 보여준다.
 */
function SmsConfigCard({ canWrite, jobs }: { canWrite: boolean; jobs: BatchJob[] }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['batch-admin', 'sms-config'], queryFn: batchAdminApi.smsConfig });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ apiUrl: '', sender: '', timeoutMs: '5000', apiKey: '' });
  const [submitted, setSubmitted] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      batchAdminApi.saveSmsConfig({
        apiUrl: form.apiUrl,
        sender: form.sender,
        timeoutMs: Number(form.timeoutMs),
        // 빈 값은 아예 보내지 않는다 - 서버가 "안 바꿈"으로 읽는다.
        ...(form.apiKey.trim() === '' ? {} : { apiKey: form.apiKey.trim() }),
      }),
    onSuccess: async () => {
      setEditing(false);
      setSubmitted(false);
      setForm((f) => ({ ...f, apiKey: '' }));
      await qc.invalidateQueries({ queryKey: ['batch-admin', 'sms-config'] });
    },
  });

  const config = q.data;
  const ownSenders = Object.entries(config?.jobSenders ?? {});
  const titleOf = (name: string) => jobs.find((j) => j.name === name)?.title ?? name;
  const urlError = checkApiUrl(form.apiUrl);
  const senderError = checkPhone(form.sender);
  const timeoutError = checkTimeout(form.timeoutMs);
  const keyError = form.apiKey.trim() === '' ? undefined : checkApiKey(form.apiKey.trim());
  const hasError =
    urlError !== undefined ||
    senderError !== undefined ||
    timeoutError !== undefined ||
    keyError !== undefined;

  const startEdit = () => {
    if (!config) return;
    setForm({
      apiUrl: config.apiUrl,
      sender: config.sender,
      timeoutMs: String(config.timeoutMs),
      apiKey: '',
    });
    setSubmitted(false);
    setEditing(true);
  };

  return (
    <section className="card">
      <div className="card__head">
        <h2 className="card__title">SMS 발송 설정</h2>
        {!editing && canWrite && config?.storeReady && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={startEdit}>
            수정
          </button>
        )}
      </div>

      <p className="td-muted batch-summary">
        문자를 보내는 배치 <b>전부</b>가 이 설정으로 발송합니다. 수신번호는 배치마다 따로이고,
        발신번호도 필요하면 배치마다 따로 정할 수 있습니다(수신번호 창).
      </p>

      {q.isLoading ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} onRetry={() => q.refetch()} />
      ) : config ? (
        <>
          {!config.storeReady && (
            <div className="notice">
              설정 테이블이 아직 적용되지 않아 <b>지금은 소스에 있는 기본값으로 발송</b>됩니다.
              화면에서 고치려면 <code>migrations/012_xadmin_batch_sms_config.sql</code> 을 적용해야
              합니다.
            </div>
          )}

          <ErrorBox error={save.error} />

          {editing ? (
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(true);
                if (hasError) return;
                save.mutate();
              }}
            >
              <Field label="발송 주소" error={submitted ? urlError : undefined}>
                <input
                  className={inputCls(submitted ? urlError : undefined)}
                  value={form.apiUrl}
                  placeholder="https://w3.bizhost.co.kr/api/sms"
                  onChange={(e) => setForm({ ...form, apiUrl: e.target.value })}
                />
              </Field>

              <Field label="발신번호" error={submitted ? senderError : undefined}>
                <input
                  className={inputCls(submitted ? senderError : undefined)}
                  value={form.sender}
                  placeholder="15779848"
                  inputMode="numeric"
                  onChange={(e) => setForm({ ...form, sender: e.target.value })}
                />
              </Field>

              <p className="td-muted batch-summary">
                발신번호는 <b>발송 업체(bizhost)에 미리 등록된 번호</b>만 나갑니다(발신번호
                사전등록제). 등록되지 않은 번호로 바꾸면 오류 없이 발송만 조용히 실패합니다 —
                번호를 바꾸기 전에 등록부터 하세요.
              </p>

              <Field
                label="API 키"
                error={submitted ? keyError : undefined}
              >
                <input
                  className={inputCls(submitted ? keyError : undefined)}
                  value={form.apiKey}
                  placeholder="바꿀 때만 입력 (비우면 그대로)"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                />
              </Field>

              <Field label="응답 대기(ms)" error={submitted ? timeoutError : undefined}>
                <input
                  className={inputCls(submitted ? timeoutError : undefined)}
                  value={form.timeoutMs}
                  inputMode="numeric"
                  onChange={(e) => setForm({ ...form, timeoutMs: e.target.value })}
                />
              </Field>

              <div className="batch-actions">
                <button type="submit" className="btn btn--primary" disabled={save.isPending}>
                  {save.isPending ? '저장 중…' : '저장'}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setEditing(false);
                    setSubmitted(false);
                  }}
                >
                  취소
                </button>
              </div>
            </form>
          ) : (
            <dl className="deflist">
              <dt>발신번호</dt>
              <dd>
                <b>{formatPhone(config.sender)}</b>{' '}
                <span className="td-muted">
                  — {ownSenders.length > 0 ? '아래 배치를 뺀 나머지가' : '문자를 보내는 배치 전부가'} 이
                  번호로 보냅니다.
                </span>
                {ownSenders.map(([name, phone]) => (
                  <div key={name}>
                    {titleOf(name)}: <b>{formatPhone(phone)}</b>{' '}
                    <span className="td-muted">— 이 배치만 따로 정한 번호</span>
                  </div>
                ))}
              </dd>
              <dt>발송 주소</dt>
              <dd>
                <code>{config.apiUrl}</code>
              </dd>
              <dt>API 키</dt>
              <dd>
                <code>{config.apiKeyMasked || '(없음)'}</code>{' '}
                <span className="td-muted">— 온전한 값은 화면에 내려오지 않습니다.</span>
              </dd>
              <dt>응답 대기</dt>
              <dd>{config.timeoutMs.toLocaleString()}ms</dd>
              <dt>출처</dt>
              <dd>
                {config.fromStore ? (
                  <>
                    설정 테이블
                    {config.updatedAt && (
                      <span className="td-muted">
                        {' '}
                        — {config.updatedAt}
                        {config.updatedBy ? ` · ${config.updatedBy}` : ''}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="td-muted">소스 기본값 (아직 화면에서 고친 적 없음)</span>
                )}
              </dd>
            </dl>
          )}
        </>
      ) : null}
    </section>
  );
}

/** 저장 전 검사. 서버(batch/services/sms.config.ts)와 같은 규칙이다. */
function checkApiUrl(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return '발송 주소를 입력해주세요.';
  if (value.length > 255) return '발송 주소가 너무 깁니다.';
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return '주소 형식이 올바르지 않습니다. (예: https://w3.bizhost.co.kr/api/sms)';
  }
  if (url.protocol !== 'https:') {
    return 'https 주소만 쓸 수 있습니다. API 키가 그대로 실려 나가는 요청입니다.';
  }
  return undefined;
}

function checkApiKey(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return 'API 키를 입력해주세요.';
  if (value.length < 8) return 'API 키가 너무 짧습니다.';
  if (value.length > 255) return 'API 키가 너무 깁니다.';
  if (/[\s -]/.test(value)) return 'API 키에 공백이나 줄바꿈이 들어 있습니다.';
  return undefined;
}

function checkTimeout(raw: string): string | undefined {
  const value = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(value) || !Number.isInteger(value)) {
    return '응답 대기 시간은 정수여야 합니다.';
  }
  if (value < 1000 || value > 30000) return '응답 대기 시간은 1000~30000ms 사이여야 합니다.';
  return undefined;
}

/** 저장 전 검사. 서버(recipients.logic.ts)와 같은 규칙이다. */
function checkPhone(raw: string): string | undefined {
  const phone = raw.replace(/[^0-9]/g, '');
  if (phone === '') return '전화번호를 입력해주세요.';
  if (phone.length < 8) return '전화번호가 너무 짧습니다.';
  if (phone.length > 12) return '전화번호가 너무 깁니다.';
  if (!phone.startsWith('0') && !/^1[5678]\d{2}/.test(phone)) {
    return '0 으로 시작하거나 15xx·16xx·17xx·18xx 대표번호여야 합니다.';
  }
  return undefined;
}


/**
 * 수신번호 창 맨 위의 발신번호 한 줄.
 *
 * 따로 정하지 않은 배치는 공통 번호('SMS 발송 설정')로 나간다. 따로 정하면
 * 이 배치만 그 번호로 나가고, '공통 번호로'를 누르면 따로 정한 번호를 지운다.
 * 고치는 것은 공통 설정과 같이 최고 관리자만, 설정 표가 있을 때만 한다.
 */
function JobSender({ job, config, canWrite }: { job: BatchJob; config: SmsConfig; canWrite: boolean }) {
  const qc = useQueryClient();
  const own = Object.hasOwn(config.jobSenders, job.name) ? config.jobSenders[job.name] : undefined;
  /** null 이면 보기, 문자열이면 고치는 중인 값 */
  const [draft, setDraft] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const save = useMutation({
    mutationFn: (sender: string) => batchAdminApi.saveJobSender(job.name, sender),
    onSuccess: async () => {
      setDraft(null);
      setSubmitted(false);
      await qc.invalidateQueries({ queryKey: ['batch-admin', 'sms-config'] });
    },
  });

  const error = draft === null ? undefined : checkPhone(draft);
  const writable = canWrite && config.storeReady;

  if (draft !== null) {
    return (
      <>
        <ErrorBox error={save.error} />
        <form
          className="batch-recipient-form"
          aria-label="이 배치 발신번호"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (error) return;
            save.mutate(draft);
          }}
        >
          <Field label="이 배치 발신번호" error={submitted ? error : undefined}>
            <input
              className={inputCls(submitted ? error : undefined)}
              value={draft}
              placeholder="031-846-5881"
              inputMode="numeric"
              onChange={(e) => setDraft(e.target.value)}
            />
          </Field>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? '저장 중…' : '저장'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDraft(null);
              setSubmitted(false);
            }}
          >
            취소
          </button>
        </form>
        <p className="td-muted batch-summary">
          발송 업체(bizhost)에 <b>미리 등록된 번호</b>만 나갑니다. 등록되지 않은 번호면 오류 없이
          발송만 조용히 실패합니다.
        </p>
      </>
    );
  }

  return (
    <>
      <ErrorBox error={save.error} />
      <p className="td-muted">
        발신번호 <b>{formatPhone(own ?? config.sender)}</b> 로 나갑니다
        {own !== undefined ? ' — 이 배치만 따로 정한 번호입니다.' : " — 공통 번호입니다(아래 'SMS 발송 설정')."}
        {writable && (
          <>
            {' '}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setDraft(own ?? '');
                setSubmitted(false);
              }}
            >
              바꾸기
            </button>
            {own !== undefined && (
              <>
                {' '}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={save.isPending}
                  onClick={() => {
                    if (confirm(`이 배치도 공통 번호(${formatPhone(config.sender)})로 보낼까요?`)) {
                      save.mutate('');
                    }
                  }}
                >
                  공통 번호로
                </button>
              </>
            )}
          </>
        )}
      </p>
    </>
  );
}

const LIST_LABEL: Record<ListKey, string> = { default: '기본', ads: '광고 담당' };

/**
 * 수신번호 추가·수정·삭제.
 *
 * 목록이 비면 그 배치는 **아무에게도 보내지 않는다**(서버가 기본값으로
 * 되돌리지 않는다) - 지웠다고 생각한 사람에게 계속 문자가 가는 편이 더 나쁘다.
 * 그래서 마지막 한 명을 지울 때 그 사실을 알린다.
 */
function RecipientDialog({
  job,
  canWrite,
  onClose,
}: {
  job: BatchJob;
  canWrite: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const titleId = useId();
  // 설정 카드와 같은 캐시를 쓴다(추가 요청이 나가지 않는다). 수신번호를 고치는
  // 자리에서 "어느 번호로 나가는지"가 같이 보여야 확인이 한 번에 끝난다.
  const smsConfig = useQuery({
    queryKey: ['batch-admin', 'sms-config'],
    queryFn: batchAdminApi.smsConfig,
  });
  const key = ['batch-admin', 'recipients', job.name];
  const q = useQuery({ queryKey: key, queryFn: () => batchAdminApi.recipients(job.name) });
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const [form, setForm] = useState({ listKey: 'default' as ListKey, phone: '', label: '' });
  const [editing, setEditing] = useState<BatchRecipient | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Esc 로 닫힌다 - 이 프로젝트의 다른 겹침 화면(OrderDrawer 등)과 같은 규칙.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const phoneError = checkPhone(form.phone);
  const storeReady = q.data?.storeReady ?? false;
  // report-shop-msg-real-lms 만 목록이 둘이다. 나머지는 고를 이유가 없어 감춘다.
  const multiList = job.name === 'report-shop-msg-real-lms';

  const add = useMutation({
    mutationFn: () =>
      batchAdminApi.addRecipient(job.name, { ...form, label: form.label, enabled: true }),
    onSuccess: async () => {
      setForm({ listKey: form.listKey, phone: '', label: '' });
      setSubmitted(false);
      await refresh();
    },
  });

  const update = useMutation({
    mutationFn: (r: BatchRecipient) =>
      batchAdminApi.updateRecipient(job.name, r.id, {
        listKey: r.listKey,
        phone: r.phone,
        label: r.label,
        enabled: r.enabled,
      }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => batchAdminApi.removeRecipient(job.name, id),
    onSuccess: refresh,
  });

  const rows = q.data?.recipients ?? [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title" id={titleId}>
          {job.title} — {job.sends?.toUpperCase()} 수신번호
        </h2>
        <p className="td-muted">{job.summary}</p>
        {smsConfig.data && <JobSender job={job} config={smsConfig.data} canWrite={canWrite} />}

        {!storeReady && (
          <div className="notice">
            테이블이 아직 적용되지 않아 <b>읽기 전용</b>입니다. 아래는 지금 발송에 쓰이는 소스
            기본값입니다.
          </div>
        )}

        {q.isLoading ? (
          <Spinner />
        ) : (
          <>
            <ErrorBox error={q.error ?? add.error ?? update.error ?? remove.error} />

            {rows.length === 0 ? (
              <p className="notice notice--warn">
                수신번호가 없습니다 — 이 배치는 <b>지금 아무에게도 보내지 않습니다.</b>
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    {multiList && <th style={{ width: 90 }}>목록</th>}
                    <th style={{ width: 140 }}>번호</th>
                    <th>이름</th>
                    <th style={{ width: 70 }}>발송</th>
                    {storeReady && canWrite && <th style={{ width: 110 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    // 테이블 적용 전(id 가 전부 0인 소스 기본값)에도 키가 겹치지 않게.
                    <tr key={r.id || `${r.listKey}-${r.phone}`}>
                      {multiList && <td>{LIST_LABEL[r.listKey]}</td>}
                      <td>
                        {editing?.id === r.id ? (
                          <input
                            className="input"
                            aria-label="번호"
                            value={editing.phone}
                            onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                          />
                        ) : (
                          formatPhone(r.phone)
                        )}
                      </td>
                      <td>
                        {editing?.id === r.id ? (
                          <input
                            className="input"
                            aria-label="이름"
                            value={editing.label}
                            onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          />
                        ) : (
                          r.label || <span className="td-muted">—</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${r.label || r.phone} 발송`}
                          checked={editing?.id === r.id ? editing.enabled : r.enabled}
                          disabled={!storeReady || !canWrite}
                          onChange={(e) =>
                            editing?.id === r.id
                              ? setEditing({ ...editing, enabled: e.target.checked })
                              : update.mutate({ ...r, enabled: e.target.checked })
                          }
                        />
                      </td>
                      {storeReady && canWrite && (
                        <td>
                          {editing?.id === r.id ? (
                            <>
                              <button
                                type="button"
                                className="btn btn--primary btn--sm"
                                disabled={checkPhone(editing.phone) !== undefined}
                                onClick={() => update.mutate(editing)}
                              >
                                저장
                              </button>{' '}
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setEditing(null)}
                              >
                                취소
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setEditing(r)}
                              >
                                수정
                              </button>{' '}
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                  const last = rows.filter((x) => x.listKey === r.listKey).length === 1;
                                  const warn = last
                                    ? '\n\n마지막 번호입니다. 지우면 이 배치는 아무에게도 보내지 않습니다.'
                                    : '';
                                  if (confirm(`${r.label || r.phone} 을(를) 목록에서 지울까요?${warn}`)) {
                                    remove.mutate(r.id);
                                  }
                                }}
                              >
                                삭제
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {storeReady && canWrite && (
              <form
                className="batch-recipient-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSubmitted(true);
                  if (phoneError) return;
                  add.mutate();
                }}
              >
                {multiList && (
                  <Field label="목록">
                    <select
                      className="input"
                      value={form.listKey}
                      onChange={(e) => setForm({ ...form, listKey: e.target.value as ListKey })}
                    >
                      <option value="default">기본</option>
                      <option value="ads">광고 담당</option>
                    </select>
                  </Field>
                )}
                <Field label="번호" error={submitted ? phoneError : undefined}>
                  <input
                    className={inputCls(submitted ? phoneError : undefined)}
                    value={form.phone}
                    placeholder="010-0000-0000"
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </Field>
                <Field label="이름">
                  <input
                    className="input"
                    value={form.label}
                    maxLength={50}
                    placeholder="누구인지"
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                  />
                </Field>
                <button type="submit" className="btn btn--primary" disabled={add.isPending}>
                  {add.isPending ? '추가 중…' : '추가'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
